import { PriceDisplayType, Prices } from "../types/pricing/price-display.types";
import { EstimatedPrice, ProductPriceDetails } from "../types/pricing/product-price-details.types";
import { PriceDisplayGroup } from "../types/product";

export interface ResolveStablePriceDetailsParams {
  currentPriceDetails: ProductPriceDetails | undefined;
  lastValidPriceDetails: ProductPriceDetails | undefined;
  lastValidQuantity: string | undefined;
  quantity: string | undefined;
  userClearedQty: boolean;
}

export const calculateEvaluatedQuantity = (
  priceDetails: ProductPriceDetails | undefined,
  currentQuantity: string | undefined,
  lastValidQuantity: string | undefined,
): string => {
  const { quantityMetaData, prices } = priceDetails ?? {};
  const { minimumOrderableQuantity, defaultQuantity } = quantityMetaData ?? {};
  const estimatedPrices = prices?.estimatedPrices ?? {};

  const fallbackQuantity =
    defaultQuantity ?? minimumOrderableQuantity ?? Number(Object.keys(estimatedPrices)[0] ?? 0);

  const hasValidPriceData = prices?.estimatedPrices;

  if (priceDetails?.priceDisplayGroup === PriceDisplayGroup.RANGED && !currentQuantity) {
    return (minimumOrderableQuantity ?? fallbackQuantity).toString();
  }
  if (
    hasValidPriceData &&
    currentQuantity &&
    Number(currentQuantity) > 0 &&
    estimatedPrices[Number(currentQuantity)]
  ) {
    return currentQuantity;
  }
  if (lastValidQuantity && Number(lastValidQuantity) > 0 && estimatedPrices[Number(lastValidQuantity)]) {
    return lastValidQuantity;
  }
  return fallbackQuantity.toString();
};

export const calculateResolvedPrices = (
  priceDetails: ProductPriceDetails | undefined,
  evaluatedQuantity: string,
): Prices | null => {
  if (!priceDetails || isNaN(Number(evaluatedQuantity))) return null;

  const { displayStrategy, prices, quantityMetaData, alternateQuantityMetaData } = priceDetails;
  const { estimatedPrices } = prices;
  const { minimumOrderableQuantity, maximumOrderableQuantity } = alternateQuantityMetaData ?? quantityMetaData ?? {};
  const isUnitRange = displayStrategy === PriceDisplayType.UnitRangePrice;
  const isDifferentialUnitRangePrice = displayStrategy === PriceDisplayType.DifferentialUnitRangePrice;
  const isDifferentialPrice = displayStrategy === PriceDisplayType.DifferentialPrice;
  const isDifferentialUnitPrice = displayStrategy === PriceDisplayType.UnitDifferentialPrice;

  // Handle Unit Range display strategy separately
  if (minimumOrderableQuantity && maximumOrderableQuantity) {
    const priceAtMin = estimatedPrices[minimumOrderableQuantity];
    const priceAtMax = estimatedPrices[maximumOrderableQuantity];
    if (isUnitRange) {
      return getUnitRangePrices(priceAtMin, priceAtMax);
    }
    if (isDifferentialUnitRangePrice) {
      return getDifferentialRangePrices(priceAtMin, priceAtMax);
    }
  }
  const currentEstimatedPrice = estimatedPrices[Number(evaluatedQuantity)];

  if (isDifferentialPrice || isDifferentialUnitPrice) {
    return getDifferentialPrices(currentEstimatedPrice);
  }

  return getStandardPrices(currentEstimatedPrice);
};

const getDifferentialRangePrices = (priceAtMinQty: EstimatedPrice, priceAtMaxQty: EstimatedPrice) => ({
  priceDifferenceAtMaximumPrice: priceAtMinQty?.alternativeOptionPrices?.differentialUnitListPrice,
  discountedPriceDifferenceAtMaximumPrice: priceAtMinQty?.alternativeOptionPrices?.differentialUnitDiscountedPrice,
  priceDifferenceAtMinimumPrice: priceAtMaxQty?.alternativeOptionPrices?.differentialUnitListPrice,
  discountedPriceDifferenceAtMinimumPrice: priceAtMaxQty?.alternativeOptionPrices?.differentialUnitDiscountedPrice,
  logoProcessingChargeListPrice: priceAtMinQty?.logoProcessingChargeListPrice,
  logoProcessingChargeDiscountedPrice: priceAtMinQty?.logoProcessingChargeDiscountedPrice,
});

const getUnitRangePrices = (minPrice: EstimatedPrice, maxPrice: EstimatedPrice) => ({
  highestListPrice: minPrice?.unitListPrice,
  highestDiscountedPrice: minPrice?.unitDiscountedPrice,
  lowestListPrice: maxPrice?.unitListPrice,
  lowestDiscountedPrice: maxPrice?.unitDiscountedPrice,
  setupFeeListPrice: minPrice?.setupFeeListPrice,
  setupFeeDiscountedPrice: minPrice?.setupFeeDiscountedPrice,
  logoProcessingChargeListPrice: minPrice?.logoProcessingChargeListPrice,
  logoProcessingChargeDiscountedPrice: minPrice?.logoProcessingChargeDiscountedPrice,
});

const getDifferentialPrices = (current: EstimatedPrice) => {
  return {
    differentialListPrice: current?.alternativeOptionPrices?.differentialListPrice,
    differentialDiscountedPrice: current?.alternativeOptionPrices?.differentialDiscountedPrice,
    differentialUnitListPrice: current?.alternativeOptionPrices?.differentialUnitListPrice,
    differentialUnitDiscountedPrice: current?.alternativeOptionPrices?.differentialUnitDiscountedPrice,
    logoProcessingChargeListPrice: current?.logoProcessingChargeListPrice,
    logoProcessingChargeDiscountedPrice: current?.logoProcessingChargeDiscountedPrice,
  };
};

const getStandardPrices = (current: EstimatedPrice) => {
  return {
    totalListPrice: current.totalListPrice,
    totalDiscountedPrice: current.totalDiscountedPrice,
    unitListPrice: current.unitListPrice,
    unitDiscountedPrice: current.unitDiscountedPrice,
    baseListPrice: current.baseListPrice,
    baseDiscountedPrice: current.baseDiscountedPrice,
    setupFeeListPrice: current.setupFeeListPrice,
    setupFeeDiscountedPrice: current.setupFeeDiscountedPrice,
    volumeDiscount: current.discountMetaData?.volumeDiscountPercentage,
    savingsPercentage: current.discountMetaData?.savingsPercentage,
    logoProcessingChargeListPrice: current.logoProcessingChargeListPrice,
    logoProcessingChargeDiscountedPrice: current.logoProcessingChargeDiscountedPrice,
  };
};

/**
 * Builds a string key from selections excluding quantity, used to detect
 * whether non-qty selections (e.g. color) changed between renders.
 */
export const getNonQtySelectionsKey = (selections: Record<string, string> | undefined): string => {
  if (!selections) return "";
  return Object.keys(selections)
    .filter(k => k.toLowerCase() !== "quantity")
    .sort()
    .map(k => `${k}:${selections[k]}`)
    .join("::");
};

/**
 * Determines which price details to display to prevent layout shift.
 * Retains the last valid qty-based price during color switches until
 * the new qty-specific response arrives.
 */
export const resolveStablePriceDetails = ({
  currentPriceDetails,
  lastValidPriceDetails,
  lastValidQuantity,
  quantity,
  userClearedQty,
}: ResolveStablePriceDetailsParams): ProductPriceDetails | undefined => {
  const currentHasEstimatedPrices = currentPriceDetails?.prices?.estimatedPrices;
  const hasLastValidQtyPrice =
    lastValidQuantity &&
    Number(lastValidQuantity) > 0 &&
    lastValidPriceDetails?.prices?.estimatedPrices;

  const currentIsRangeWithoutQtyPrice =
    currentHasEstimatedPrices &&
    currentPriceDetails?.displayStrategy === PriceDisplayType.UnitRangePrice &&
    !(quantity && Number(quantity) > 0 && currentPriceDetails?.prices?.estimatedPrices?.[Number(quantity)]);

  const shouldRetainLastValidPrice = currentIsRangeWithoutQtyPrice && hasLastValidQtyPrice && !userClearedQty;

  if (shouldRetainLastValidPrice) return lastValidPriceDetails;
  if (currentHasEstimatedPrices) return currentPriceDetails;
  return lastValidPriceDetails;
};
